/**
 * Earliest selectable session date from establishment / school-year rules.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import { getEstablishment } from '@/services/establishments.service';

/** Fallback look-back window when the establishment has no `maxPastDays`. */
export const DEFAULT_MAX_PAST_DAYS = 7;

/**
 * Earliest YYYY-MM-DD allowed for session create/update from the selected
 * establishment's `maxPastDays` (default 7 when no establishment / loading).
 */
export function useSessionMinDate(establishmentId?: string | null): {
  maxPastDays: number;
  minSessionDate: string;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: ['establishments', establishmentId, 'max-past-days'],
    queryFn: () => getEstablishment(establishmentId!),
    enabled: !!establishmentId,
  });

  const maxPastDays =
    typeof data?.maxPastDays === 'number' && data.maxPastDays >= 0
      ? data.maxPastDays
      : DEFAULT_MAX_PAST_DAYS;

  const minSessionDate = useMemo(
    () => format(subDays(new Date(), maxPastDays), 'yyyy-MM-dd'),
    [maxPastDays],
  );

  return { maxPastDays, minSessionDate, isLoading: !!establishmentId && isLoading };
}
